import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reportCardAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiDownload, FiEye, FiAward, FiFilter } from 'react-icons/fi';
import { format } from 'date-fns';

const ViewReportCards = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const examIdFromUrl = searchParams.get('exam_id');
  
  const [reportCards, setReportCards] = useState([]);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedExam, setSelectedExam] = useState(examIdFromUrl || '');
  const [selectedClass, setSelectedClass] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [examsRes, classesRes] = await Promise.all([
        reportCardAPI.getOfflineExams(),
        masterAPI.getClasses()
      ]);

      if (examsRes.data.success) setExams(examsRes.data.data);
      if (classesRes.data.success) setClasses(classesRes.data.data);
      
      // Fetch all report cards
      fetchReportCards();
    } catch (error) {
      toast.error('Error loading data');
      console.error(error);
    }
  };

  const fetchReportCards = async () => {
    try {
      setLoading(true);
      
      // Get all report cards by fetching from database
      // Since there's no dedicated API endpoint, we'll need to get them per exam
      const allReports = [];
      
      if (selectedExam) {
        // Get students for the exam's class
        const examData = exams.find(e => e.id === parseInt(selectedExam));
        if (examData) {
          const studentsRes = await masterAPI.getStudents({ class_id: examData.class_id });
          if (studentsRes.data.success) {
            for (const student of studentsRes.data.data) {
              try {
                const reportRes = await reportCardAPI.getReportCard(student.id, selectedExam);
                if (reportRes.data.success && reportRes.data.data) {
                  allReports.push({
                    ...reportRes.data.data,
                    student_name: student.name,
                    student_email: student.email
                  });
                }
              } catch (error) {
                // Student doesn't have report card yet, skip
              }
            }
          }
        }
      }
      
      setReportCards(allReports);
    } catch (error) {
      toast.error('Error fetching report cards');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedExam) {
      fetchReportCards();
    }
  }, [selectedExam]);

  const getGradeBadgeColor = (grade) => {
    if (grade?.includes('A')) return 'bg-green-100 text-green-800';
    if (grade?.includes('B')) return 'bg-blue-100 text-blue-800';
    if (grade?.includes('C')) return 'bg-yellow-100 text-yellow-800';
    if (grade?.includes('D')) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const handleDownloadPDF = async (report) => {
    try {
      toast.info('Generating PDF...');
      
      const response = await reportCardAPI.downloadReportCard(report.student_id, report.offline_exam_id);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const examName = exams.find(e => e.id === report.offline_exam_id)?.exam_name || 'Exam';
      const studentName = report.student_name || 'Student';
      link.download = `ReportCard_${studentName}_${examName}.pdf`.replace(/[^A-Za-z0-9_\-\.]/g, '_');
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const filteredReportCards = reportCards.filter(report =>
    report.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.student_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">View Report Cards</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">View and download generated report cards</p>
        </div>
        <button
          onClick={() => navigate('/report-cards/exams')}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to Exams
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Exam *</label>
            <select
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            >
              <option value="">Select an exam</option>
              {exams.map(exam => (
                <option key={exam.id} value={exam.id}>
                  {exam.exam_name} - {exam.class_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Student</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
            />
          </div>
        </div>
      </div>

      {/* Report Cards List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {!selectedExam ? (
          <div className="text-center py-12">
            <FiFilter size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Select an Exam</h3>
            <p className="text-gray-600">Please select an exam to view report cards</p>
          </div>
        ) : loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading report cards...</p>
          </div>
        ) : filteredReportCards.length === 0 ? (
          <div className="text-center py-12">
            <FiAward size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Report Cards Found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery ? 'No report cards match your search' : 'No report cards have been generated for this exam yet'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate(`/report-cards/generate?exam_id=${selectedExam}`)}
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Generate Report Cards
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Percentage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReportCards.map(report => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{report.student_name}</div>
                      <div className="text-sm text-gray-500">{report.student_email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className="font-semibold text-gray-900">
                        {report.total_marks_obtained} / {report.total_marks}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className="font-semibold text-gray-900">
                        {parseFloat(report.percentage).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getGradeBadgeColor(report.overall_grade)}`}>
                        {report.overall_grade}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100 text-yellow-800 font-bold">
                        #{report.rank}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.attendance_percentage ? `${parseFloat(report.attendance_percentage).toFixed(1)}%` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {report.generated_at ? format(new Date(report.generated_at), 'MMM dd, yyyy') : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownloadPDF(report)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Download PDF"
                        >
                          <FiDownload size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {filteredReportCards.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Total Students</p>
            <p className="text-2xl font-bold text-gray-900">{filteredReportCards.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Average Percentage</p>
            <p className="text-2xl font-bold text-gray-900">
              {(filteredReportCards.reduce((sum, r) => sum + parseFloat(r.percentage), 0) / filteredReportCards.length).toFixed(2)}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Pass Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {((filteredReportCards.filter(r => !r.overall_grade?.includes('F')).length / filteredReportCards.length) * 100).toFixed(1)}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Avg Attendance</p>
            <p className="text-2xl font-bold text-gray-900">
              {filteredReportCards[0]?.attendance_percentage 
                ? (filteredReportCards.reduce((sum, r) => sum + parseFloat(r.attendance_percentage || 0), 0) / filteredReportCards.length).toFixed(1) + '%'
                : 'N/A'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewReportCards;
